// services/frontend/src/utils/axiosInterceptor.js

import axios from 'axios';
import {
  clearAuth,
  getOrCreateDeviceId,
  getToken,
  isAppEnvironment,
  isTokenExpired,
  refreshAccessToken,
  requestTokenFromApp,
} from './auth';

let isRefreshing = false;
let failedQueue = [];

const getBackendDetail = (response) =>
  response?.data?.detail || response?.data?.message || '';

const shouldTreatAsAuthRecovery = (error, originalRequest) => {
  const status = error.response?.status;
  if (status === 401) {
    return true;
  }

  if (status !== 403) {
    return false;
  }

  const token = getToken();
  const hasAuthHeader = Boolean(
    originalRequest?.headers?.Authorization ||
      originalRequest?.headers?.authorization ||
      token
  );

  if (!hasAuthHeader) {
    return false;
  }

  // 빈 403은 백엔드가 손상/만료 토큰을 권한 오류로 응답하는 경우가 있어
  // 토큰 파싱이 실패하거나 만료된 경우에만 refresh 경로로 보냅니다.
  if (getBackendDetail(error.response)) {
    return false;
  }

  return !token || isTokenExpired(token);
};

/**
 * 대기 중인 요청들을 처리
 * @param {Error|null} error - 에러 객체 (refresh 실패 시)
 * @param {string|null} token - 새로운 Access Token (refresh 성공 시)
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Axios Interceptor 설정
 * 
 * [Why]
 * 모든 API 요청에 대해 자동으로 인증 헤더를 추가하고,
 * 401 에러 발생 시 자동으로 토큰을 갱신하여 사용자 경험을 개선합니다.
 * 
 * [How]
 * 1. Request Interceptor: 모든 요청에 Authorization 헤더와 X-Device-Id 헤더 추가
 * 2. Response Interceptor: 401 에러 시 Refresh Token으로 자동 갱신 후 재시도
 */
export const setupAxiosInterceptors = () => {
  // Request Interceptor: 모든 요청에 인증 헤더 추가
  axios.interceptors.request.use(
    (config) => {
      const token = getToken();
      const deviceId = getOrCreateDeviceId();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // X-Device-Id 헤더 추가 (이미 있으면 덮어쓰지 않음)
      if (!config.headers['X-Device-Id']) {
        config.headers['X-Device-Id'] = deviceId;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response Interceptor: 401 에러 시 자동 토큰 갱신
  axios.interceptors.response.use(
    (response) => {
      // 정상 응답은 그대로 반환
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // 세션 복구 대상(401, 또는 손상/만료 토큰에 대한 빈 403)이 아니거나
      // 이미 재시도한 요청이면 그대로 에러 반환
      if (!shouldTreatAsAuthRecovery(error, originalRequest) || originalRequest._retry) {
        return Promise.reject(error);
      }

      // 로그인/회원가입/토큰갱신 API는 자동 갱신 대상에서 제외
      const isAuthEndpoint = 
        originalRequest.url?.includes('/api/v1/auth/login') ||
        originalRequest.url?.includes('/api/v1/auth/register') ||
        originalRequest.url?.includes('/api/v1/auth/refresh');

      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      // 이미 토큰 갱신 중이면 대기열에 추가
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        let newToken = null;
        
        // 앱 환경이면 앱으로 토큰 갱신 요청
        if (isAppEnvironment()) {
          newToken = await requestTokenFromApp();
          
          if (newToken) {
            // 대기 중인 요청들 처리
            processQueue(null, newToken);
            
            // 원래 요청 재시도
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          } else {
            // 앱 토큰 갱신 실패 → 앱 로그인 페이지로
            processQueue(new Error('App token refresh failed'), null);
            clearAuth();
            
            if (window.location.pathname !== '/app/login') {
              window.location.href = '/app/login';
            }
            
            return Promise.reject(error);
          }
        }
        
        // 웹 환경이면 기존 Refresh Token 방식 사용
        const success = await refreshAccessToken();

        if (success) {
          newToken = getToken();

          // 대기 중인 요청들 처리
          processQueue(null, newToken);

          // 원래 요청 재시도
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        } else {
          // Propagate a service failure instead of the original401 to other consumers.
          if (getToken()) {
            const unavailable = new Error('인증 서버 확인을 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.');
            unavailable.response = { status: 503 };
            processQueue(unavailable, null);
            return Promise.reject(unavailable);
          }
          processQueue(error, null);
          // Definitive rejection was already cleared by authStore.
          clearAuth();
          
          // 앱/웹 환경에 따라 다른 페이지로 이동
          const loginPath = isAppEnvironment() ? '/app/login' : '/';
          if (window.location.pathname !== loginPath) {
            window.location.href = loginPath;
          }
          
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // 401 에러는 정상적인 토큰 만료이므로 로그 출력 안 함
        // 네트워크 오류 등 예외적인 경우만 로그 출력
        if (refreshError?.response?.status !== 401) {
          console.error('[Axios Interceptor] 토큰 갱신 중 예상치 못한 오류:', refreshError);
        }

        processQueue(refreshError, null);
        if (!isAppEnvironment() && getToken()) {
          return Promise.reject(refreshError);
        }
        clearAuth();

        // 앱/웹 환경에 따라 다른 페이지로 이동
        const loginPath = isAppEnvironment() ? '/app/login' : '/';
        if (window.location.pathname !== loginPath) {
          window.location.href = loginPath;
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};
